/* eslint-disable react/jsx-max-depth */
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { OnOffSwitch, OnOffSwitchSize, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { IVerificationFilterState } from "./verification-filters.types";
import type { IPublisherOption } from "./verification-widget.utils";
import { VerificationFilters } from "./VerificationFilters";

const InfoIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    cursor: pointer;
`;

interface IVerificationFiltersBarProps {
    filters: IVerificationFilterState;
    onFiltersChange: (next: IVerificationFilterState) => void;
    publisherOptions: IPublisherOption[];
    campaignOptions: IPublisherOption[];
    isPublishersLoading: boolean;
    isCampaignsLoading: boolean;
    campaignSearch: string;
    onCampaignSearch: (value: string) => void;
    onlyScreenshots: boolean;
    onOnlyScreenshotsToggle: () => void;
}

export const VerificationFiltersBar = ({
    filters,
    onFiltersChange,
    publisherOptions,
    campaignOptions,
    isPublishersLoading,
    isCampaignsLoading,
    campaignSearch,
    onCampaignSearch,
    onlyScreenshots,
    onOnlyScreenshotsToggle,
}: IVerificationFiltersBarProps) => {
    const { translate } = useTranslate();

    return (
        <Flex
            alignItems="center"
            gap="16px"
            margin="0 0 16px 0"
            data-automation="verification-filters-bar"
        >
            <VerificationFilters
                state={filters}
                onChange={onFiltersChange}
                publisherOptions={publisherOptions}
                campaignOptions={campaignOptions}
                isPublishersLoading={isPublishersLoading}
                isCampaignsLoading={isCampaignsLoading}
                campaignSearch={campaignSearch}
                onCampaignSearch={onCampaignSearch}
            />
            <Flex
                as="label"
                alignItems="center"
                gap="8px"
                cursor="pointer"
            >
                <OnOffSwitch
                    isSelected={onlyScreenshots}
                    size={OnOffSwitchSize.small}
                    onClick={onOnlyScreenshotsToggle}
                    data-automation="verification-only-screenshots-toggle"
                />

                <Flex
                    alignItems="center"
                    gap="4px"
                >
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate("ad.intelligence.verification.filters.only_screenshots.label")}
                    </Text>
                    <Tooltip content={translate("ad.intelligence.verification.filters.only_screenshots.tooltip")}>
                        <InfoIconWrapper>
                            <Info24px
                                size="sm"
                                color={colorsPalettes.carbon[300]}
                            />
                        </InfoIconWrapper>
                    </Tooltip>
                </Flex>
            </Flex>
        </Flex>
    );
};
